import type { Language } from '@eformulieren/engine'
import { isOpenAdvies } from '@eformulieren/model'
import { type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { BusyButton } from './BusyButton.js'
import { CountBadge, countName } from './CountBadge.js'
import { ConfirmQuestion } from './ConfirmQuestion.js'
import { Dialog } from './Dialog.js'
import { keepEscapeInDialog } from './dialogPlace.js'
import { InterestReview } from './InterestReview.js'
import {
  type AdviceFilter,
  type AdviceReactionView,
  type AdviceRequestView,
  type AdviceTarget,
  adviceAnchorTitle,
  adviceAt,
  adviceNews,
  filterAdvice,
  filterCounts,
  targetOf,
} from './adviceModel.js'
import { type ActionOutcome, type ShareNotice, adviceStateWord, formatDay } from './shareModel.js'
import { type Bilingual, formatDateTime } from '../language/text.js'

/**
 * Het paneel "Advies" rechts naast het formulier (R-184, R-184b, R-184c, R-139, R-140, ontwerp 7.4a,
 * mockups 01 en 06 van advies).
 *
 * **Alleen aan de interne kant, en alleen als er advies is.** Of het er staat beslist de pagina: de
 * externe kant krijgt het nooit, ook geen lege plek (R-184b). Dit component kent de kant niet en toont
 * wat het krijgt.
 *
 * **Een kaart per verzoek**, met het anker als link (springt naar de vraag of sectie en markeert hem,
 * `onAnker`), de adviseur zoals de pod hem noemt, de stand als tekst in een chip (kleur is nooit de
 * enige drager, R-139), het gesprek oudste eerst, en de handelingen die bij de stand horen: bij
 * gevraagd Herinneren, Termijn wijzigen (in een modale dialoog sinds 1 november 2026) en Intrekken; bij beantwoord een reactievak met zichtbaar
 * label, Afronden en Intrekken. Een geëindigd verzoek houdt zijn gesprek en heeft geen handelingen.
 * Elke handeling bij de pod is een `BusyButton`; een weigering staat bij de knop of het vak, in gewone
 * taal (R-140).
 *
 * **De belangenverklaring en het oordeel van NWO** (R-184d, R-184j, R-184k): heeft de adviseur
 * verklaard, dan staat op de kaart wat hij verklaarde en zag, elk oordeel van NWO, en als de kijker
 * mag de knop NWO-oordeel geven (`InterestReview`). Ook op een kaart die door een belang eindigde: daar
 * kan NWO het verzoek weer laten lopen.
 *
 * **Nieuw advies wordt gemeld** in een `aria-live=polite`-regio: een antwoord van de adviseur, een
 * gemeld belang of een vervallen verzoek sinds de vorige lezing; niet bij het openen.
 *
 * **Openen op een kaart.** `toon` met een verzoek (Bekijk advies in de lijst Wie heeft toegang) of met
 * een plek (het label "1 advies" bij een vraag) zet het filter op Alles, markeert die kaarten en zet de
 * focus op de eerste; `teller` telt op bij elke vraag, zodat dezelfde vraag nog eens werkt.
 */

export interface AdvicePanelProps {
  readonly taal: Language
  readonly verzoeken: readonly AdviceRequestView[]
  /** Of de kijker advies kan vragen (`kanVragen` uit `GET /advies`): dan staat de knop er. */
  readonly kanVragen: boolean
  /** De sectie waar de lezer nu is, voor het filter Deze sectie. */
  readonly actieveSectie?: string
  readonly toon?: { readonly verzoek?: string; readonly doel?: AdviceTarget; readonly teller: number }
  readonly onVragen: () => void
  readonly onAnker: (target: AdviceTarget) => void
  readonly onReageren: (id: string, tekst: string) => Promise<ActionOutcome>
  readonly onAfronden: (id: string) => Promise<ActionOutcome>
  readonly onHerinneren: (id: string) => Promise<ActionOutcome>
  readonly onIntrekken: (id: string) => Promise<ActionOutcome>
  /** Zonder deze prop geen Termijn wijzigen. */
  readonly onTermijn?: (id: string, termijn: string | null) => Promise<ActionOutcome>
  /**
   * Of de kijker alleen de belangenverklaring kan beoordelen en verder niets met een verzoek doet:
   * functioneel beheer in een opening (R-155f, R-184k). Dan geen reactievak en geen handelingen.
   */
  readonly alleenOordeel?: boolean
  /** Het oordeel van NWO over de belangenverklaring (R-184k). Zonder deze prop geen knop om te oordelen. */
  readonly onBelangOordeel?: (id: string, uitkomst: 'geen-belang' | 'belang', toelichting: string) => Promise<ActionOutcome>
  /**
   * Of het paneel nieuw advies zelf meldt (standaard ja). In het formulier staat de melding in een regio
   * buiten het paneel (`SidePanel.melding`), want het paneel kan daar verborgen zijn; dan staat dit uit.
   */
  readonly meldtZelf?: boolean
}

const T = {
  kop: { nl: 'Advies', en: 'Advice' },
  vragen: { nl: 'Advies vragen', en: 'Ask for advice' },
  filter: { nl: 'Welk advies', en: 'Which advice' },
  // Het aantal staat naast het label als vak (`CountBadge`) en in de naam: "Open: 2".
  alles: { nl: 'Alles', en: 'All' },
  open: { nl: 'Open', en: 'Open' },
  sectie: { nl: 'Deze sectie', en: 'This section' },
  geen: { nl: 'Er is nog geen advies gevraagd over dit formulier.', en: 'No advice has been asked about this form yet.' },
  geenOpen: { nl: 'Er staat geen advies open.', en: 'There is no open request for advice.' },
  geenSectie: { nl: 'Bij deze sectie is geen advies gevraagd.', en: 'No advice has been asked about this section.' },
  termijn: { nl: 'antwoord graag voor {d}', en: 'answer requested by {d}' },
  gesprek: { nl: 'Gesprek', en: 'Conversation' },
  reactie: { nl: 'Vervolgvraag of reactie', en: 'Follow-up question or reply' },
  leeg: { nl: 'Schrijf eerst uw reactie.', en: 'Write your reply first.' },
  versturen: { nl: 'Versturen', en: 'Send' },
  bezigVersturen: { nl: 'Bezig met versturen…', en: 'Sending…' },
  afronden: { nl: 'Afronden', en: 'Complete' },
  bezigAfronden: { nl: 'Bezig met afronden…', en: 'Completing…' },
  afgerond: { nl: 'Het advies over {a} is afgerond. De adviseur heeft geen toegang meer.', en: 'The advice about {a} is complete. The adviser no longer has access.' },
  herinneren: { nl: 'Herinneren', en: 'Remind' },
  bezigHerinneren: { nl: 'Bezig met herinneren…', en: 'Sending reminder…' },
  herinnerd: { nl: 'De herinnering is gevraagd. NWO stuurt de e-mail.', en: 'The reminder has been requested. NWO sends the email.' },
  intrekken: { nl: 'Intrekken', en: 'Withdraw' },
  belangToelichting: { nl: 'Toelichting van de adviseur: {t}', en: "The adviser's explanation: {t}" },
  intrekVraag: { nl: 'Adviesvraag intrekken?', en: 'Withdraw the request for advice?' },
  intrekGevolg: { nl: '{w} kan dit formulier daarna niet meer lezen en niet meer antwoorden.', en: '{w} can no longer read this form or answer afterwards.' },
  intrekKnop: { nl: 'Adviesvraag intrekken', en: 'Withdraw request for advice' },
  bezigIntrekken: { nl: 'Bezig met intrekken…', en: 'Withdrawing…' },
  ingetrokken: { nl: 'De adviesvraag over {a} is ingetrokken.', en: 'The request for advice about {a} has been withdrawn.' },
  annuleren: { nl: 'Annuleren', en: 'Cancel' },
  termijnWijzigen: { nl: 'Termijn wijzigen', en: 'Change deadline' },
  termijnLabel: { nl: 'Antwoord graag voor', en: 'Answer requested by' },
  termijnHulp: { nl: 'Laat leeg voor geen termijn.', en: 'Leave empty for no deadline.' },
  opslaan: { nl: 'Opslaan', en: 'Save' },
  bezigOpslaan: { nl: 'Bezig met opslaan…', en: 'Saving…' },
  termijnGewijzigd: { nl: 'De termijn is gewijzigd.', en: 'The deadline has been changed.' },
} as const satisfies Readonly<Record<string, Bilingual>>

export function AdvicePanel(props: AdvicePanelProps): ReactNode {
  const { taal: language, verzoeken: requests, kanVragen: canAsk, actieveSectie: activeSection, toon: show } = props
  const headerId = useId()
  const [filter, setFilter] = useState<AdviceFilter>('alles')
  const [status, setStatus] = useState('')
  const [news, setNews] = useState('')
  const cards = useRef(new Map<string, HTMLElement>())

  // Wat er nieuw is tegenover de vorige lezing; de eerste lezing meldt niets.
  const previous = useRef<readonly AdviceRequestView[] | undefined>(undefined)
  useEffect(() => {
    const before = previous.current
    previous.current = requests
    if (before === undefined) return
    const said = adviceNews(before, requests, language)
    if (said.length > 0) setNews(said.join(' '))
  }, [requests, language])

  const chosen = show === undefined ? new Set<string>() : new Set(show.verzoek !== undefined ? [show.verzoek] : show.doel === undefined ? [] : adviceAt(requests, show.doel).map((request) => request.id))
  const chosenFirst = requests.find((request) => chosen.has(request.id))?.id
  // Bij elke vraag om te tonen: eerst het filter op Alles, dan in de volgende render de focus op de
  // eerste gekozen kaart. Niet bij elke lezing van de pod: alleen als de teller verandert.
  const counter = show?.teller
  const [focusFor, setFocusFor] = useState<number | undefined>(undefined)
  useEffect(() => {
    if (counter === undefined) return
    setFilter('alles')
    setFocusFor(counter)
  }, [counter])
  useEffect(() => {
    if (focusFor === undefined || filter !== 'alles' || chosenFirst === undefined) return
    const element = cards.current.get(chosenFirst)
    if (element === undefined) return
    element.scrollIntoView({ block: 'nearest' })
    element.focus()
    setFocusFor(undefined)
  }, [focusFor, filter, chosenFirst])

  const counts = filterCounts(requests, activeSection)
  const shown = filterAdvice(requests, filter, activeSection)
  const empty = requests.length === 0 ? T.geen : filter === 'open' ? T.geenOpen : filter === 'sectie' ? T.geenSectie : T.geen

  return (
    <aside className="ef-advies" aria-labelledby={headerId} lang={language}>
      <div className="ef-advies__kop">
        <h2 id={headerId} className="ef-advies__titel">
          {T.kop[language]}
        </h2>
        {canAsk ? (
          <button type="button" className="ef-actieknop" data-soort="primair" aria-haspopup="dialog" onClick={props.onVragen}>
            {T.vragen[language]}
          </button>
        ) : null}
      </div>
      {requests.length === 0 ? null : (
        <div className="ef-advies__filter" role="group" aria-label={T.filter[language]}>
          {(['alles', 'open', 'sectie'] as const).map((option) => (
            <button
              key={option}
              type="button"
              className="ef-advies__filterknop"
              aria-pressed={filter === option}
              aria-label={countName(T[option][language], [String(counts[option])])}
              onClick={() => setFilter(option)}
            >
              {T[option][language]}
              <CountBadge>{String(counts[option])}</CountBadge>
            </button>
          ))}
        </div>
      )}
      <p role="status" className="ef-advies__status">
        {status}
      </p>
      {props.meldtZelf === false ? null : (
        <div aria-live="polite" className="ef-sr-only">
          {news}
        </div>
      )}
      {shown.length === 0 ? (
        <p className="ef-advies__leeg">{empty[language]}</p>
      ) : (
        <ol className="ef-advies__lijst">
          {shown.map((request) => (
            <li key={request.id}>
              <AdviceCard
                {...props}
                verzoek={request}
                gekozen={chosen.has(request.id)}
                onStatus={setStatus}
                cardRef={(element) => {
                  if (element === null) cards.current.delete(request.id)
                  else cards.current.set(request.id, element)
                }}
              />
            </li>
          ))}
        </ol>
      )}
    </aside>
  )
}

type Mode = 'rust' | 'intrekken' | 'termijn'

function AdviceCard(
  props: AdvicePanelProps & {
    readonly verzoek: AdviceRequestView
    readonly gekozen: boolean
    readonly onStatus: (text: string) => void
    readonly cardRef: (element: HTMLElement | null) => void
  },
): ReactNode {
  const { taal: language, verzoek: request } = props
  const titleId = useId()
  const replyId = useId()
  const errorId = useId()
  const dateId = useId()
  const dateHelpId = useId()
  const [mode, setMode] = useState<Mode>('rust')
  const [reply, setReply] = useState('')
  const [notice, setNotice] = useState<(ShareNotice & { readonly bij: 'reactie' | 'knoppen' | 'termijn' }) | undefined>(undefined)
  const [deadline, setDeadline] = useState(request.termijn ?? '')
  // Na Annuleren of Opslaan de focus terug op de knop waarmee het begon. Die knop is in de tussentijd
  // uit beeld geweest, dus een ref op de nieuwe knop en niet op het element van toen.
  const withdrawButton = useRef<HTMLButtonElement>(null)
  const deadlineButton = useRef<HTMLButtonElement>(null)
  const dateField = useRef<HTMLInputElement>(null)
  const [restore, setRestore] = useState<'intrekken' | 'termijn' | undefined>(undefined)
  useEffect(() => {
    if (restore === undefined || mode !== 'rust') return
    ;(restore === 'intrekken' ? withdrawButton : deadlineButton).current?.focus()
    setRestore(undefined)
  }, [restore, mode])
  const [savingDeadline, setSavingDeadline] = useState(false)

  const title = adviceAnchorTitle(request.anker, language)
  const target = targetOf(request.anker)
  const open = isOpenAdvies(request.toestand)
  const asked = request.toestand === 'gevraagd'
  const answered = request.toestand === 'beantwoord' && props.alleenOordeel !== true

  async function act(work: () => Promise<ActionOutcome>, done: string, at: 'reactie' | 'knoppen' | 'termijn'): Promise<boolean> {
    setNotice(undefined)
    const outcome = await work()
    if (outcome.soort === 'gelukt') {
      if (done !== '') props.onStatus(done)
      return true
    }
    setNotice({ ...outcome.melding, bij: at })
    return false
  }

  const noticeAt = (at: 'reactie' | 'knoppen' | 'termijn') =>
    notice?.bij === at ? (
      <p id={errorId} role="alert" className="ef-veld__melding--hard">
        {notice.tekst}
      </p>
    ) : null
  const replyInvalid = notice?.bij === 'reactie'

  return (
    <article
      ref={props.cardRef}
      tabIndex={-1}
      className="ef-adviesverzoek"
      aria-labelledby={titleId}
      data-gekozen={props.gekozen ? 'ja' : undefined}
      data-toestand={request.toestand}
    >
      <div className="ef-adviesverzoek__kop">
        <h3 className="ef-adviesverzoek__titel">
          {target === undefined ? (
            <span id={titleId}>{title}</span>
          ) : (
            <a
              id={titleId}
              className="ef-adviesverzoek__anker"
              href={`#${target.veldId ?? target.sectieId}`}
              onClick={(event) => {
                event.preventDefault()
                props.onAnker(target)
              }}
            >
              {title}
            </a>
          )}
        </h3>
        <span className="ef-adviesstand" data-stand={request.toestand}>
          {adviceStateWord(request.toestand, language)}
        </span>
      </div>
      <p className="ef-adviesverzoek__wie">
        {[request.adviseur, request.termijn === null ? undefined : T.termijn[language].replace('{d}', formatDay(request.termijn, language))]
          .filter((part) => part !== undefined)
          .join(' · ')}
      </p>
      <AdviceConversation taal={language} reacties={request.reacties} />
      {/* Waarom het verzoek eindigde, in de woorden van de adviseur (opnamen van advies: de toelichting
          ging wel mee in het bericht, maar stond niet op de kaart). */}
      {request.verklaring !== undefined ? (
        <InterestReview
          taal={language}
          toestand={request.toestand}
          verklaring={request.verklaring}
          oordelen={request.oordelen ?? []}
          kanBeoordelen={request.kanBeoordelen === true && props.onBelangOordeel !== undefined}
          onOordeel={(uitkomst, toelichting) => props.onBelangOordeel?.(request.id, uitkomst, toelichting) ?? Promise.resolve({ soort: 'gelukt' })}
          onStatus={props.onStatus}
        />
      ) : request.toestand === 'belang-gemeld' && request.belangToelichting !== undefined ? (
        <p className="ef-adviesverzoek__belang">{T.belangToelichting[language].replace('{t}', request.belangToelichting)}</p>
      ) : null}
      {answered ? (
        <div className="ef-adviesverzoek__reactie">
          <label htmlFor={replyId} className="ef-veld__label">
            <span className="ef-veld__labeltekst">{T.reactie[language]}</span>
          </label>
          <textarea
            id={replyId}
            className="ef-invoer ef-adviesverzoek__vak"
            rows={3}
            value={reply}
            onChange={(event) => setReply(event.target.value)}
            {...(replyInvalid ? { 'aria-invalid': true, 'aria-describedby': errorId } : {})}
          />
          {noticeAt('reactie')}
        </div>
      ) : null}
      {mode === 'intrekken' ? (
        <ConfirmQuestion
          kop={T.intrekVraag[language]}
          consequence={T.intrekGevolg[language].replace('{w}', request.adviseur)}
          proceed={T.intrekKnop[language]}
          soort="gevaar"
          cancel={T.annuleren[language]}
          busyText={T.bezigIntrekken[language]}
          onProceed={async () => {
            if (await act(() => props.onIntrekken(request.id), T.ingetrokken[language].replace('{a}', title), 'knoppen')) setMode('rust')
          }}
          onCancel={() => {
            setMode('rust')
            setRestore('intrekken')
          }}
        />
      ) : null}
      {mode === 'termijn' ? (
        // De termijn in een modale dialoog (opdracht van 1 november 2026); tot dan klapte het veld open
        // in de kaart. De knoppen van de kaart blijven staan, zodat de focus terug kan naar Termijn wijzigen.
        <div className="ef-dialooghouder" onKeyDown={keepEscapeInDialog}>
        <Dialog
          taal={language}
          titel={T.termijnWijzigen[language]}
          className="ef-adviesverzoek__termijn"
          onSluit={() => {
            // Tijdens het opslaan wacht sluiten tot de pod antwoordt.
            if (savingDeadline) return
            setMode('rust')
            setNotice(undefined)
            setDeadline(request.termijn ?? '')
            setRestore('termijn')
          }}
          secundair={{ label: T.annuleren[language], uitgeschakeld: savingDeadline }}
          primair={{
            label: T.opslaan[language],
            bezigTekst: T.bezigOpslaan[language],
            onActie: async () => {
              const change = props.onTermijn
              if (change === undefined) return
              setSavingDeadline(true)
              const saved = await act(() => change(request.id, deadline === '' ? null : deadline), T.termijnGewijzigd[language], 'termijn').finally(() => setSavingDeadline(false))
              if (saved) {
                setMode('rust')
                setRestore('termijn')
              }
            },
          }}
        >
          <p className="ef-veld__hulptekst">{title}</p>
          <label htmlFor={dateId} className="ef-veld__label">
            <span className="ef-veld__labeltekst">{T.termijnLabel[language]}</span>
          </label>
          <p id={dateHelpId} className="ef-veld__hulptekst">
            {T.termijnHulp[language]}
          </p>
          <input
            ref={dateField}
            data-autofocus
            id={dateId}
            type="date"
            className="ef-invoer"
            value={deadline}
            onChange={(event) => setDeadline(event.target.value)}
            aria-describedby={[dateHelpId, notice?.bij === 'termijn' ? errorId : undefined].filter((part) => part !== undefined).join(' ')}
            {...(notice?.bij === 'termijn' ? { 'aria-invalid': true } : {})}
          />
          {noticeAt('termijn')}
        </Dialog>
        </div>
      ) : null}
      {(mode === 'rust' || mode === 'termijn') && open && props.alleenOordeel !== true ? (
        <>
          <div className="ef-adviesverzoek__acties">
            {answered ? (
              <BusyButton
                className="ef-actieknop"
                data-soort="primair"
                busyText={T.bezigVersturen[language]}
                onAction={async () => {
                  const text = reply.trim()
                  if (text === '') {
                    setNotice({ tekst: T.leeg[language], secties: [], bij: 'reactie' })
                    return
                  }
                  if (await act(() => props.onReageren(request.id, text), '', 'reactie')) setReply('')
                }}
              >
                {T.versturen[language]}
              </BusyButton>
            ) : null}
            {answered ? (
              <BusyButton
                className="ef-actieknop"
                data-soort="secundair"
                busyText={T.bezigAfronden[language]}
                onAction={() => act(() => props.onAfronden(request.id), T.afgerond[language].replace('{a}', title), 'knoppen')}
              >
                {T.afronden[language]}
              </BusyButton>
            ) : null}
            {asked ? (
              <BusyButton
                className="ef-actieknop"
                data-soort="secundair"
                busyText={T.bezigHerinneren[language]}
                onAction={() => act(() => props.onHerinneren(request.id), T.herinnerd[language], 'knoppen')}
              >
                {T.herinneren[language]}
              </BusyButton>
            ) : null}
            {asked && props.onTermijn !== undefined ? (
              <button
                ref={deadlineButton}
                type="button"
                className="ef-actieknop"
                data-soort="secundair"
                aria-haspopup="dialog"
                onClick={() => {
                  setNotice(undefined)
                  setDeadline(request.termijn ?? '')
                  setMode('termijn')
                }}
              >
                {T.termijnWijzigen[language]}
              </button>
            ) : null}
            <button
              ref={withdrawButton}
              type="button"
              className="ef-actieknop"
              data-soort="gevaar"
              onClick={() => {
                setNotice(undefined)
                setMode('intrekken')
              }}
            >
              {T.intrekken[language]}
            </button>
          </div>
          {noticeAt('knoppen')}
        </>
      ) : null}
    </article>
  )
}

/** Het gesprek, oudste eerst: wie, wanneer, en de tekst met zijn regels. */
export function AdviceConversation({
  taal: language,
  reacties: reactions,
  label,
}: {
  readonly taal: Language
  readonly reacties: readonly AdviceReactionView[]
  readonly label?: string
}): ReactNode {
  if (reactions.length === 0) return null
  return (
    <ol className="ef-adviesgesprek" aria-label={label ?? T.gesprek[language]}>
      {reactions.map((reaction) => (
        <li key={reaction.id} className="ef-adviesgesprek__reactie" data-rol={reaction.rol}>
          <p className="ef-adviesgesprek__wie">
            {reaction.wie} · <time dateTime={reaction.moment}>{momentText(reaction.moment, language)}</time>
          </p>
          <p className="ef-adviesgesprek__tekst">{reaction.tekst}</p>
        </li>
      ))}
    </ol>
  )
}

/** "3 dec 10:14": de dag en de tijd in Nederland, zoals de mockup. */
function momentText(moment: string, language: Language): string {
  return formatDateTime(moment, language, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Amsterdam',
  })
}
